import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { FiSun, FiMoon, FiChevronRight, FiArrowLeft, FiBriefcase } from 'react-icons/fi';
import loginImage from '../../assets/login-img.png';
import caycoLogo from '../../assets/Cayco_logo.png';
import { ORG_SELECT_KEY } from './Login';

const SelectOrganization = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { selectOrganizationLogin, user, loading: authLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [selectingId, setSelectingId] = useState(null);

  const sessionPayload = useMemo(() => {
    if (location.state?.selectionToken && location.state?.organizations) {
      return location.state;
    }
    try {
      const raw = sessionStorage.getItem(ORG_SELECT_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [location.state]);

  const organizations = sessionPayload?.organizations || [];
  const selectionToken = sessionPayload?.selectionToken;
  const email = sessionPayload?.email;

  useEffect(() => {
    if (!authLoading && user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!authLoading && !user && (!selectionToken || !organizations.length)) {
      navigate('/login', { replace: true });
    }
  }, [authLoading, user, selectionToken, organizations.length, navigate]);

  const handleSelect = async (organizationId) => {
    if (!selectionToken || selectingId) return;
    setSelectingId(organizationId);

    const result = await selectOrganizationLogin(selectionToken, organizationId);
    if (result.success) {
      sessionStorage.removeItem(ORG_SELECT_KEY);
      navigate('/dashboard');
    } else if (result.error?.toLowerCase().includes('expired') || result.error?.toLowerCase().includes('selection')) {
      sessionStorage.removeItem(ORG_SELECT_KEY);
      navigate('/login');
    }
    setSelectingId(null);
  };

  const handleBack = () => {
    sessionStorage.removeItem(ORG_SELECT_KEY);
    navigate('/login');
  };

  if (authLoading || !selectionToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      {/* Left side - matches Login */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-primary-600 to-primary-800 dark:from-primary-700 dark:to-primary-900 relative overflow-hidden">
        <div className="absolute top-8 right-8 z-10">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-md text-white hover:bg-white/20 transition-colors"
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? (
              <FiMoon className="h-5 w-5" />
            ) : (
              <FiSun className="h-5 w-5" />
            )}
          </button>
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <img
            src={loginImage}
            alt="Select organization"
            className="max-w-full max-h-full object-contain"
          />
        </div>
        <div className="absolute top-8 left-8">
          <Link to="/" className="inline-block">
            <img
              src={caycoLogo}
              alt="Cayco"
              className="h-10 w-auto"
            />
          </Link>
        </div>
        <div className="absolute bottom-8 left-8 right-8 text-white">
          <h3 className="text-2xl font-bold mb-2">Choose your workspace</h3>
          <p className="text-primary-100 dark:text-primary-200">
            Select the organization you want to enter. You can switch later by signing in again.
          </p>
        </div>
      </div>

      {/* Right side - org list */}
      <div className="w-full md:w-1/2 flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute top-4 right-4 md:hidden">
          <button
            type="button"
            onClick={toggleTheme}
            className="p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? (
              <FiMoon className="h-5 w-5" />
            ) : (
              <FiSun className="h-5 w-5" />
            )}
          </button>
        </div>

        <div className="max-w-md w-full space-y-8">
          <div className="text-center md:hidden mb-4">
            <Link to="/" className="inline-block">
              <span className="text-2xl font-bold text-primary-600 dark:text-primary-400">Cayco</span>
            </Link>
          </div>

          <div>
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 mb-4"
            >
              <FiArrowLeft className="h-4 w-4" />
              Back to sign in
            </button>
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white">
              Select an organization
            </h2>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {email
                ? <>Organizations available for <span className="font-medium text-gray-800 dark:text-gray-200">{email}</span></>
                : 'Choose which organization to enter'}
            </p>
          </div>

          <div className="max-h-[min(28rem,55vh)] overflow-y-auto overscroll-contain space-y-3 pr-1 -mr-1">
            {organizations.map((org) => {
              const isSelecting = selectingId === org.organizationId;
              const disabled = Boolean(selectingId);
              return (
                <button
                  key={org.organizationId}
                  type="button"
                  disabled={disabled}
                  onClick={() => handleSelect(org.organizationId)}
                  className={`w-full text-left flex items-center gap-3 px-4 py-4 rounded-xl border transition-all duration-200
                    bg-white dark:bg-gray-800
                    border-gray-200 dark:border-gray-700
                    hover:border-primary-500 dark:hover:border-primary-400
                    hover:shadow-md hover:bg-primary-50/40 dark:hover:bg-primary-900/20
                    focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400
                    disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:shadow-none
                    ${isSelecting ? 'border-primary-500 dark:border-primary-400 ring-2 ring-primary-500/30' : ''}`}
                >
                  <span className="flex-shrink-0 h-11 w-11 rounded-lg bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 flex items-center justify-center">
                    <FiBriefcase className="h-5 w-5" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-base font-semibold text-gray-900 dark:text-white truncate">
                      {org.name}
                    </span>
                    <span className="block text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                      Role: {org.role || 'Member'}
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-primary-600 dark:text-primary-400">
                    {isSelecting ? (
                      <span className="inline-block h-5 w-5 border-2 border-primary-600 dark:border-primary-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <FiChevronRight className="h-5 w-5" />
                    )}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400">
            Forgot your Organization ID?{' '}
            <Link to="/forgot-organization-id" className="text-primary-600 dark:text-primary-400 hover:text-primary-500 dark:hover:text-primary-300 font-medium">
              Click here
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SelectOrganization;
